import React from 'react';

interface AirflowPatternProps {
  className?: string;
  variant?: 'light' | 'dark';
}

/**
 * Halton Brand Airflow SVG Pattern Component
 * 
 * A reusable decorative pattern that represents airflow/ventilation,
 * aligned with the Halton brand visual identity.
 * 
 * @param className - Optional additional CSS class names
 * @param variant - 'light' for dark backgrounds (white strokes), 'dark' for light backgrounds (blue strokes)
 */
const AirflowPattern: React.FC<AirflowPatternProps> = ({ 
  className = '', 
  variant = 'light' 
}) => {
  // Define gradient colors based on variant
  const gradientId = `airflowGradient-${variant}`;
  
  // Light variant: white/cyan for dark backgrounds
  // Dark variant: Halton Blue/Deep Blue for light backgrounds
  const gradientStops = variant === 'light' ? {
    start: 'rgba(255, 255, 255, 0.4)',
    middle: 'rgba(36, 213, 197, 0.3)',  // Cool Green
    end: 'rgba(255, 255, 255, 0.2)'
  } : {
    start: 'rgba(36, 153, 213, 0.4)',   // Halton Blue
    middle: 'rgba(36, 213, 197, 0.3)',  // Cool Green
    end: 'rgba(33, 45, 98, 0.3)'        // Deep Blue
  };

  const variantClass = variant === 'light' ? 'airflow-pattern-light' : 'airflow-pattern-dark';

  return (
    <svg 
      className={`airflow-pattern ${variantClass} ${className}`.trim()} 
      viewBox="0 0 1440 320" 
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={gradientStops.start} />
          <stop offset="50%" stopColor={gradientStops.middle} />
          <stop offset="100%" stopColor={gradientStops.end} />
        </linearGradient>
      </defs>
      <path 
        fill="none" 
        stroke={`url(#${gradientId})`}
        strokeWidth="2"
        d="M0,160 C320,220 420,100 720,160 C1020,220 1120,100 1440,160"
      />
      <path 
        fill="none" 
        stroke={`url(#${gradientId})`}
        strokeWidth="1.5"
        d="M0,200 C280,140 380,260 680,200 C980,140 1080,260 1440,200"
        opacity="0.7"
      />
      <path 
        fill="none" 
        stroke={`url(#${gradientId})`}
        strokeWidth="1"
        d="M0,120 C360,180 460,60 760,120 C1060,180 1160,60 1440,120"
        opacity="0.5"
      />
    </svg>
  );
};

export default AirflowPattern;
